// ─────────────────────────────────────────────────────────────────────────────
// ENTITLEMENTS de la suite — PERSISTIDOS EN EL BACKEND (Postgres por el hub-api, ya desplegado).
//
// El catálogo de servicios que tiene habilitado cada usuario (por email) vive en la
// base de Solar Hub (`user_entitlements`, workflow `esquema/entitlements.json`). El
// localStorage `sh:entitlements` queda como CACHÉ/offline y se HIDRATA desde el backend
// al iniciar sesión (→ cross-device: lo que el admin asigna en un equipo lo ve el usuario
// en cualquier otro). Cuando el `api/` de FluxAI emita el JWT con `servicios[]`, esa lista
// se RECONCILIA con estas filas (misma llave email+sku).
//   Estado: 'activo' (contratado) · 'trial' (prueba) · 'demo' · 'inactivo'.
//   Los 3 primeros HABILITAN el servicio; 'inactivo' lo deja sin habilitar.
// La lectura de gateo (`serviciosLocales`) es SÍNCRONA sobre la caché; el sync es async.
// ─────────────────────────────────────────────────────────────────────────────

import { hubPost, hubConfigurado } from './hub';
import { tokenParaBackend } from './adminAuth';

const KEY = 'sh:entitlements';
export const ESTADOS = [
  { id: 'activo',   label: 'Activo (contratado)' },
  { id: 'trial',    label: 'Prueba (trial)' },
  { id: 'demo',     label: 'Demo' },
  { id: 'inactivo', label: 'Inactivo' },
];
const HABILITA = new Set(['activo', 'trial', 'demo']); // estados que habilitan el servicio

const norm = (e) => (e || '').toLowerCase().trim();
// 🔑 Prefiere el token de ADMINISTRACIÓN (el único FIRMADO por el hub-api) y cae al de la suite.
// Desde que `exigeAdmin` falla cerrado (2026-08-29), el `alg:none` que acuña `mintToken` da 401 en
// las ops de administración: mandarlo sería dejar el panel del dueño muerto después de que se
// provisione el admin.
const sessionToken = () => tokenParaBackend();

function read() {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
}
function write(o) {
  try {
    localStorage.setItem(KEY, JSON.stringify(o));
    window.dispatchEvent(new Event('solarhub-entitlements'));
  } catch { /* storage no disponible */ }
}

/** Mapa { sku: estado } asignado a un email (desde la caché local). */
export function getEntitlements(email) {
  return read()[norm(email)] || {};
}

/** Asigna (o cambia) el estado de un servicio para un email. Optimista en caché + persiste en backend. */
export function setEntitlement(email, sku, estado) {
  const e = norm(email);
  if (!e || !sku) return;
  const all = read();
  all[e] = { ...(all[e] || {}), [sku]: estado };
  write(all);
  if (hubConfigurado()) {
    hubPost('entitlements', { op: 'set', email: e, sku, estado, by: norm(decodeEmailFromToken()), token: sessionToken() })
      .catch(() => { /* best-effort: la caché ya refleja el cambio */ });
  }
}

/** Quita la asignación de un servicio a un email. Optimista en caché + persiste en backend. */
export function removeEntitlement(email, sku) {
  const e = norm(email);
  const all = read();
  if (all[e]) { delete all[e][sku]; if (!Object.keys(all[e]).length) delete all[e]; write(all); }
  if (hubConfigurado()) {
    hubPost('entitlements', { op: 'remove', email: e, sku, token: sessionToken() })
      .catch(() => { /* best-effort */ });
  }
}

/** SKUs HABILITADOS localmente para un email (estados activo/trial/demo) — lee la caché. */
export function serviciosLocales(email) {
  const m = getEntitlements(email);
  return Object.entries(m).filter(([, st]) => HABILITA.has(st)).map(([sku]) => sku);
}

/** Todas las asignaciones (caché), para listarlas en el panel admin. */
export function allAssignments() {
  const all = read();
  const rows = [];
  for (const [email, svcs] of Object.entries(all)) {
    for (const [sku, estado] of Object.entries(svcs)) rows.push({ email, sku, estado });
  }
  return rows;
}

// ── Sincronización con el backend (Postgres, por el hub-api) ──────────────────────────

/** Hidrata la caché de UN usuario desde el backend (llamar al iniciar sesión). Cross-device. */
export async function syncEntitlements(email) {
  const e = norm(email);
  if (!e || !hubConfigurado()) return;
  try {
    const r = await hubPost('entitlements', { op: 'get', email: e, token: sessionToken() });
    if (!r || !r.ok || !Array.isArray(r.entitlements)) return;
    const all = read();
    all[e] = {};
    for (const row of r.entitlements) { if (row.email && norm(row.email) === e && row.sku) all[e][row.sku] = row.estado; }
    if (!Object.keys(all[e]).length) delete all[e];
    write(all);
  } catch { /* offline → se queda con la caché */ }
}

/** Hidrata TODAS las asignaciones desde el backend (panel admin). Reemplaza la caché completa. */
export async function syncAllEntitlements() {
  if (!hubConfigurado()) return;
  try {
    const r = await hubPost('entitlements', { op: 'list', token: sessionToken() });
    if (!r || !r.ok || !Array.isArray(r.entitlements)) return;
    const all = {};
    for (const row of r.entitlements) {
      if (!row.email || !row.sku) continue;
      const e = norm(row.email);
      (all[e] = all[e] || {})[row.sku] = row.estado;
    }
    write(all);
  } catch { /* offline */ }
}

// Lee el email del JWT de sesión sin importar auth.js (evita ciclo). Best-effort.
function decodeEmailFromToken() {
  try {
    const t = sessionToken();
    const p = t.split('.')[1];
    if (!p) return '';
    const j = JSON.parse(decodeURIComponent(escape(atob(p.replace(/-/g, '+').replace(/_/g, '/')))));
    return j.email || '';
  } catch { return ''; }
}
